import type { Voice } from 'ml-text-service-client';
import { useMemo, useState, useRef, JSX } from 'react';
import { useSetState } from 'react-use';

import { Text, Input } from '@videoblocks/design-system';
import CloseIcon from '@videoblocks/react-icons/Close';
import SearchIcon from '@videoblocks/react-icons/Search';

import Pagination from '../../../../../components/Pagination';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import MultiselectDropdown from './MultiselectDropdown';
import VoiceCard from './VoiceCard';
import { LANGUAGE_CODE_TO_NAME, getVoiceLanguageCodes, useVoiceoverFilters } from './utils';

export type VoiceOverFilter = {
    gender: string[];
    accent: string[];
    tone: string[];
    useCase: string[];
    language: string[];
};

const ITEMS_PER_PAGE = 21;

function VoiceList({
    selectedVoice,
    setSelectedVoice,
    playingVoiceId,
    setPlayingVoiceId,
}: {
    selectedVoice: Voice['voice_id'] | null;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    playingVoiceId: Voice['voice_id'] | null;
    setPlayingVoiceId: (voiceId: Voice['voice_id'] | null) => void;
}): JSX.Element {
    const { voices } = useVoiceoverContext();
    const [voiceOverFilter, updateVoiceOverFilter] = useSetState<VoiceOverFilter>({
        gender: [],
        accent: [],
        tone: [],
        useCase: [],
        language: [],
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, _setCurrentPage] = useState(1);

    const setCurrentPage = (page: number) => {
        _setCurrentPage(page);
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const { gender, accent, tone, useCase, language } = useVoiceoverFilters(voices);

    const isAccentDisabled = voiceOverFilter.language.some((name) => name !== 'English');
    const isLanguageDisabled = voiceOverFilter.accent.length > 0;

    const filteredVoices = useMemo(() => {
        const hasActiveFilters = Object.values(voiceOverFilter).some((filterArray) => filterArray.length > 0);

        if (!hasActiveFilters && !searchQuery) {
            return voices;
        }

        const lowerCaseQuery = searchQuery.toLowerCase();

        return voices.filter((voice) => {
            const matchesGender = voiceOverFilter.gender.length === 0 || voiceOverFilter.gender.includes(voice.gender);
            const matchesAccent = voiceOverFilter.accent.length === 0 || voiceOverFilter.accent.includes(voice.accent);
            const matchesTone = voiceOverFilter.tone.length === 0 || voiceOverFilter.tone.includes(voice.descriptive);
            const matchesUseCase =
                voiceOverFilter.useCase.length === 0 || voiceOverFilter.useCase.includes(voice.use_case);
            const matchesLanguage =
                voiceOverFilter.language.length === 0 ||
                getVoiceLanguageCodes(voice).some((code) =>
                    voiceOverFilter.language.includes(LANGUAGE_CODE_TO_NAME[code] ?? ''),
                );

            const matchesSearch =
                !searchQuery ||
                voice.name.toLowerCase().includes(lowerCaseQuery) ||
                voice.description.toLowerCase().includes(lowerCaseQuery) ||
                voice.accent.toLowerCase().includes(lowerCaseQuery) ||
                voice.descriptive.toLowerCase().includes(lowerCaseQuery) ||
                voice.gender.toLowerCase().includes(lowerCaseQuery) ||
                voice.use_case.toLowerCase().includes(lowerCaseQuery);

            return matchesGender && matchesAccent && matchesTone && matchesUseCase && matchesLanguage && matchesSearch;
        });
    }, [voices, voiceOverFilter, searchQuery]);

    const paginatedVoices = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredVoices.slice(startIndex, endIndex);
    }, [filteredVoices, currentPage]);

    const updateFilter = (key: keyof VoiceOverFilter, newValues: string[]) => {
        updateVoiceOverFilter({
            [key]: newValues,
        });
        setCurrentPage(1);
    };

    const clearFilter = (key: keyof VoiceOverFilter) => updateFilter(key, []);

    return (
        <>
            <div
                className="flex justify-between mt-8 mb-5 flex-col lg:flex-row scroll-mt-[150px] lg:scroll-mt-[86px]"
                ref={containerRef}
                id="voice-list"
            >
                <Text
                    fontSize="lg"
                    className="mb-4 lg:mb-0 lg:my-4 whitespace-nowrap self-start lg:self-center pr-4"
                    asChild
                >
                    <h2>Core Voices</h2>
                </Text>
                <div role="search" aria-label="Voice library" className="flex gap-2 sm:gap-4 flex-wrap">
                    <MultiselectDropdown
                        selected={voiceOverFilter.language}
                        options={language}
                        onChange={(newLanguage) => updateFilter('language', newLanguage)}
                        onClear={() => clearFilter('language')}
                        header="Language"
                        disabled={isLanguageDisabled}
                    />
                    <MultiselectDropdown
                        selected={voiceOverFilter.accent}
                        options={accent}
                        onChange={(newAccent) => updateFilter('accent', newAccent)}
                        onClear={() => clearFilter('accent')}
                        header="Accent"
                        disabled={isAccentDisabled}
                    />
                    <MultiselectDropdown
                        selected={voiceOverFilter.gender}
                        options={gender}
                        onChange={(newGender) => updateFilter('gender', newGender)}
                        onClear={() => clearFilter('gender')}
                        header="Gender"
                    />
                    <MultiselectDropdown
                        selected={voiceOverFilter.useCase}
                        options={useCase}
                        onChange={(newUseCase) => updateFilter('useCase', newUseCase)}
                        onClear={() => clearFilter('useCase')}
                        header="Use Case"
                    />
                    <MultiselectDropdown
                        selected={voiceOverFilter.tone}
                        options={tone}
                        onChange={(newTone) => updateFilter('tone', newTone)}
                        onClear={() => clearFilter('tone')}
                        header="Tone"
                    />
                    <div className="bg-neutral-dark-200 border border-neutral-dark-300 px-4 h-14 rounded-md flex items-center gap-2 justify-between cursor-pointer hover:bg-neutral-dark-400 text-sm flex-1 min-w-[185px]">
                        <SearchIcon className="h-4 w-4 shrink-0 fill-current" />
                        <Input
                            keyboardFocusOnly
                            tone="dark"
                            size="sm"
                            id="voice-search"
                            role="searchbox"
                            aria-label="Search voices"
                            className="bg-transparent placeholder:text-white grow h-full min-w-0 rounded-sm px-0 py-0"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search library..."
                            autoComplete="off"
                        />
                        <CloseIcon
                            onClick={() => setSearchQuery('')}
                            className={`h-3 w-3 shrink-0 fill-current ${searchQuery ? 'block' : 'invisible'}`}
                        />
                    </div>
                </div>
            </div>
            <div>
                {paginatedVoices.length === 0 && (
                    <div className="flex gap-2 mt-2">
                        <SearchIcon className="h-4 w-4 fill-current" />
                        <Text fontWeight="regular">No voices matched your search. Please try again.</Text>
                    </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 mb-8">
                    {paginatedVoices.map((voice) => (
                        <VoiceCard
                            key={voice.voice_id}
                            voice={voice}
                            selectedVoice={selectedVoice}
                            setSelectedVoice={setSelectedVoice}
                            playingVoiceId={playingVoiceId}
                            setPlayingVoiceId={setPlayingVoiceId}
                        />
                    ))}
                </div>
            </div>
            {filteredVoices.length > ITEMS_PER_PAGE && (
                <Pagination
                    resultsPerPage={ITEMS_PER_PAGE}
                    totalResults={filteredVoices.length}
                    goTo={setCurrentPage}
                    currentPage={currentPage}
                    darkMode
                />
            )}
        </>
    );
}

export default VoiceList;
